import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { hooks } from '../hooks';
import { text } from '../text';
import { utils } from '../utils';
import { theme } from '../constants';
import { components } from '../components';
import axiosInstance from '../services/axiosInstance';
import { useLanguage } from '../context/LanguageContext';
import { compressImage } from '../utils/imageUtils';
import { getCurrentLocation } from '../utils/locationUtils';
import { MdFilterList, MdAccessTime, MdCheckCircle, MdError, MdWarning, MdEventNote, MdCameraAlt, MdClose, MdLocationOn, MdCheckBox, MdCheckBoxOutlineBlank, MdWifi, MdWifiOff } from 'react-icons/md';
import { isNative, getWifiInfo, checkMockLocation } from '../services/capacitorService';
import { savePhotoForQueue } from '../services/offlineFileService';
import { offlineQueue } from '../services/offlineQueueService';
import { setPendingCount } from '../store/slices/offlineSlice';
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import InfiniteScroll from 'react-infinite-scroll-component';

const FilterListIcon = MdFilterList as any;
const CheckBoxIcon = MdCheckBox as any;
const CheckBoxOutlineBlankIcon = MdCheckBoxOutlineBlank as any;
const AccessTimeIcon = MdAccessTime as any;
const CheckCircleIcon = MdCheckCircle as any;
const ErrorIcon = MdError as any;
const WarningIcon = MdWarning as any;
const EventNoteIcon = MdEventNote as any;
const CameraIcon = MdCameraAlt as any;
const CloseIcon = MdClose as any;
const LocationIcon = MdLocationOn as any;
const WifiIcon = MdWifi as any;
const WifiOffIcon = MdWifiOff as any;


export const Attendance: React.FC = () => {
  const navigate = hooks.useAppNavigate();
  const routeLocation = useLocation();
  const { t: translate, language } = useLanguage();
  const t = translate.attendance;
  const tCorr = (translate.attendance as any)?.correction || {};
  const { authSlice, offlineSlice } = hooks.useAppSelector((s: any) => s);
  const { isOnline } = offlineSlice;
  const dispatch = hooks.useAppDispatch();

  const [attendances, setAttendances] = useState<any[]>([]);
  const [todayStatus, setTodayStatus] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [appSettings, setAppSettings] = useState<any>(null);

  // State untuk filter (Default 30 hari terakhir)
  const today = new Date();
  const lastMonth = new Date();
  lastMonth.setDate(today.getDate() - 30);
  // Tanggal lokal perangkat, bukan toISOString() (UTC) — di WIB sebelum jam 07:00 toISOString
  // masih tanggal kemarin, sehingga record hari ini (termasuk catatan HR) tidak ikut terambil.
  const toLocalDateStr = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const [startDate, setStartDate] = useState<string>(toLocalDateStr(lastMonth));
  const [endDate, setEndDate] = useState<string>(toLocalDateStr(today));

  // Correction form state
  const [showCorrectionModal, setShowCorrectionModal] = useState(false);
  const [corrDate, setCorrDate] = useState('');
  const [corrCheckIn, setCorrCheckIn] = useState('');
  const [corrCheckOut, setCorrCheckOut] = useState('');
  const [corrBreakOut, setCorrBreakOut] = useState('');
  const [corrBreakIn, setCorrBreakIn] = useState('');
  const [corrReason, setCorrReason] = useState('');
  const [corrEvidence, setCorrEvidence] = useState<File | null>(null);
  const [corrEvidencePreview, setCorrEvidencePreview] = useState<string | null>(null);
  const [isSubmittingCorr, setIsSubmittingCorr] = useState(false);
  const [corrections, setCorrections] = useState<any[]>([]);
  const corrEvidenceRef = useRef<HTMLInputElement>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const [photo, setPhoto] = useState<File | null>(null);
  const [showPhotoSourceSheet, setShowPhotoSourceSheet] = useState(false);
  const [isBusinessTrip, setIsBusinessTrip] = useState<boolean>(false);
  const [note, setNote] = useState<string>('');
  const [location, setLocation] = useState<{lat: number, lng: number} | null>(null);
  const [address, setAddress] = useState<string>('');
  const [locationError, setLocationError] = useState<string>('');
  const [isFetchingLocation, setIsFetchingLocation] = useState<boolean>(true);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [photoPreviewUrl, setPhotoPreviewUrl] = useState<string | null>(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [isCompressing, setIsCompressing] = useState<boolean>(false);
  const [geofenceWarning, setGeofenceWarning] = useState<string | null>(null);
  const [nearestPoiInfo, setNearestPoiInfo] = useState<{ id: number; name: string; distance: number; inRange: boolean } | null>(null);
  const [wifiInfo, setWifiInfo] = useState<{ ssid: string; bssid: string } | null>(null);
  const imageBaseUrl = process.env.REACT_APP_IMAGE_BASE_URL || 'https://apiteratasks.teralintas.com/';

  const [alertConfig, setAlertConfig] = useState<{
    isOpen: boolean; title: string; message: string; type: 'success' | 'error' | 'warning'; onClose?: () => void;
  }>({ isOpen: false, title: '', message: '', type: 'success' });

  const [confirmConfig, setConfirmConfig] = useState<{
    isOpen: boolean; message: string; onConfirm: () => void;
  }>({ isOpen: false, message: '', onConfirm: () => {} });

  const showConfirm = (message: string, onConfirm: () => void) => {
    setConfirmConfig({ isOpen: true, message, onConfirm });
  };

  const hideConfirm = () => setConfirmConfig(prev => ({ ...prev, isOpen: false }));

  const showAlert = (title: string, message: string, type: 'success' | 'error' | 'warning' = 'success', onClose?: () => void) => {
    setAlertConfig({ isOpen: true, title, message, type, onClose });
  };

  const hideAlert = () => {
    const onClose = alertConfig.onClose;
    setAlertConfig((prev) => ({ ...prev, isOpen: false }));
    if (onClose) onClose();
  };

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!photo) {
      setPhotoPreviewUrl(null);
      return;
    }

    const objectUrl = URL.createObjectURL(photo);
    setPhotoPreviewUrl(objectUrl);

    // Cleanup function to revoke the object URL to prevent memory leaks
    return () => URL.revokeObjectURL(objectUrl);
  }, [photo]);

  // Fungsi untuk mengambil lokasi secara real-time
  const fetchCurrentLocation = async () => {
    setIsFetchingLocation(true);
    setGeofenceWarning(null);
    try {
      const locationData = await getCurrentLocation({ addressFormat: 'full' });
      setLocation({ lat: locationData.lat, lng: locationData.lng });
      setAddress(locationData.address || t.addressNotFound);
      setLocationError('');

      try {
        const validationResponse = await axiosInstance.get(`/attendances/validate-location?latitude=${locationData.lat}&longitude=${locationData.lng}`);
        const vd = validationResponse.data;
        if (vd && !vd.inRange) {
          setGeofenceWarning(vd.message);
        }
        if (vd?.nearestPoi) {
          setNearestPoiInfo(vd.nearestPoi);
        }
      } catch (validationError) {
        console.error("Geofence validation check failed on load:", validationError);
      }

      return { lat: locationData.lat, lng: locationData.lng, address: locationData.address || t.addressNotFound, accuracy: locationData.accuracy };
    } catch (error: any) {
      const errorMsg = error.message || t.alerts.locationError;
      setLocationError(errorMsg);
      throw new Error(errorMsg);
    } finally {
      setIsFetchingLocation(false);
    }
  };

  useEffect(() => {
    fetchCurrentLocation().catch(console.error);
  }, []);

  useEffect(() => {
    if (!isNative) return;
    getWifiInfo().then(info => {
      if (info && info.ssid && info.ssid !== '<unknown ssid>') {
        setWifiInfo({ ssid: info.ssid, bssid: info.bssid });
      } else {
        setWifiInfo(null);
      }
    });
  }, []);

  useEffect(() => {
    fetchTodayStatus();
    fetchAppSettings();
    if ((routeLocation.state as any)?.openCorrection) {
      setShowCorrectionModal(true);
    }
  }, []);

  useEffect(() => {
    setOffset(0);
    fetchHistory(0);
    fetchCorrections();
  }, [startDate, endDate]);

  useEffect(() => {
    if (offset > 0) {
      fetchHistory(offset);
    }
  }, [offset]);

  const fetchTodayStatus = async () => {
    try {
      const response = await axiosInstance.get('/attendances/today');
      setTodayStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch today status:', error);
    }
  };

  const fetchAppSettings = async () => {
    try {
      const response = await axiosInstance.get('/settings/app');
      setAppSettings(response.data);
    } catch (error) {
      console.error('Failed to fetch app settings:', error);
    }
  };

  const fetchHistory = async (currentOffset: number = 0) => {
    try {
      if (currentOffset === 0) setIsLoading(true);
      else setIsLoadingMore(true);

      const response = await axiosInstance.get(`/attendances?start_date=${startDate}&end_date=${endDate}&limit=10&offset=${currentOffset}`);
      const newData = response.data;

      if (newData.length < 10) setHasMore(false);
      else setHasMore(true);

      if (currentOffset === 0) {
        setAttendances(newData);
      } else {
        setAttendances(prev => {
          const existingIds = new Set(prev.map(a => a.id));
          const uniqueNew = newData.filter((a: any) => !existingIds.has(a.id));
          return [...prev, ...uniqueNew];
        });
      }
    } catch (error) {
      console.error('Failed to fetch history:', error);
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  };

  const fetchCorrections = async () => {
    try {
      const res = await axiosInstance.get(`/attendance-corrections/my?start_date=${startDate}&end_date=${endDate}`);
      setCorrections(res.data);
    } catch (e) {
      console.error('Failed to fetch corrections:', e);
    }
  };

  const resetCorrForm = () => {
    setCorrDate('');
    setCorrCheckIn('');
    setCorrCheckOut('');
    setCorrBreakOut('');
    setCorrBreakIn('');
    setCorrReason('');
    setCorrEvidence(null);
    setCorrEvidencePreview(null);
  };

  const handleCorrEvidenceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setCorrEvidence(file);
      setCorrEvidencePreview(URL.createObjectURL(file));
    }
    if (corrEvidenceRef.current) corrEvidenceRef.current.value = '';
  };

  const toLocalTimestamp = (date: string, time: string) => {
    const [year, month, day] = date.split('-').map(Number);
    const [hour, minute] = time.split(':').map(Number);
    return new Date(year, month - 1, day, hour, minute, 0).toISOString();
  };

  const submitCorrectionForm = async () => {
    if (!corrDate || !corrReason.trim()) {
      showAlert(translate.common.warning, tCorr.validation?.required || 'Tanggal dan alasan wajib diisi.', 'warning');
      return;
    }
    if (!corrCheckIn && !corrCheckOut && !corrBreakOut && !corrBreakIn) {
      showAlert(translate.common.warning, tCorr.validation?.timeRequired || 'Minimal satu waktu (masuk, keluar, atau istirahat) harus diisi.', 'warning');
      return;
    }
    setIsSubmittingCorr(true);
    try {
      const fd = new FormData();
      fd.append('date', corrDate);
      fd.append('reason', corrReason.trim());
      if (corrCheckIn) fd.append('check_in_time', toLocalTimestamp(corrDate, corrCheckIn));

      if (corrCheckOut) {
        // Deteksi cross-midnight: jika jam checkout < jam check-in, checkout di hari berikutnya
        let effectiveCheckInHHMM = corrCheckIn;
        if (!effectiveCheckInHHMM) {
          const existingAtt = attendances.find(a => (a.date || '').split('T')[0] === corrDate)
            || (todayStatus?.date === corrDate ? todayStatus : null);
          if (existingAtt?.check_in_time) {
            const ci = new Date(existingAtt.check_in_time);
            effectiveCheckInHHMM = `${String(ci.getHours()).padStart(2, '0')}:${String(ci.getMinutes()).padStart(2, '0')}`;
          }
        }
        let checkOutDate = corrDate;
        if (effectiveCheckInHHMM && corrCheckOut < effectiveCheckInHHMM) {
          const nextDay = new Date(corrDate + 'T00:00:00');
          nextDay.setDate(nextDay.getDate() + 1);
          checkOutDate = nextDay.toISOString().split('T')[0];
        }
        fd.append('check_out_time', toLocalTimestamp(checkOutDate, corrCheckOut));
      }

      if (corrBreakOut) fd.append('break_out_time', toLocalTimestamp(corrDate, corrBreakOut));
      if (corrBreakIn) fd.append('break_in_time', toLocalTimestamp(corrDate, corrBreakIn));
      if (corrEvidence) fd.append('evidence', corrEvidence);
      await axiosInstance.post('/attendance-corrections', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      showAlert(translate.common.success, tCorr.submitSuccess || 'Permintaan koreksi berhasil diajukan!', 'success', () => {
        setShowCorrectionModal(false);
        resetCorrForm();
        fetchCorrections();
      });
    } catch (err: any) {
      const msg = err.response?.data?.message || translate.common.error;
      showAlert(translate.common.error, msg, 'error');
    } finally {
      setIsSubmittingCorr(false);
    }
  };

  const applyWatermark = (dataUrl: string): Promise<string> => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d')!;
        ctx.drawImage(img, 0, 0);

        const fontSize = Math.max(14, Math.round(img.width / 28));
        const lineHeight = Math.round(fontSize * 1.6);
        const padding = Math.round(fontSize * 0.8);

        const now = new Date();
        const dateTimeStr = now.toLocaleString(language === 'id' ? 'id-ID' : 'en-US', {
          day: '2-digit', month: '2-digit', year: 'numeric',
          hour: '2-digit', minute: '2-digit', second: '2-digit',
        });
        const locText = address || (location ? `${location.lat.toFixed(6)}, ${location.lng.toFixed(6)}` : '');

        const lines = [authSlice?.userName || '', dateTimeStr, locText].filter(Boolean);
        const bgHeight = lines.length * lineHeight + padding * 2;

        // Background transparan di bawah gambar
        ctx.fillStyle = 'rgba(0, 0, 0, 0.58)';
        ctx.fillRect(0, img.height - bgHeight, img.width, bgHeight);

        ctx.fillStyle = '#FFFFFF';
        ctx.font = `${fontSize}px sans-serif`;
        ctx.textBaseline = 'top';
        const maxWidth = img.width - padding * 2;

        lines.forEach((line, i) => {
          // Potong teks jika terlalu panjang
          let display = line;
          while (ctx.measureText(display).width > maxWidth && display.length > 8) {
            display = display.slice(0, -4) + '...';
          }
          ctx.fillText(display, padding, img.height - bgHeight + padding + i * lineHeight);
        });

        resolve(canvas.toDataURL('image/jpeg', 0.92));
      };
      img.src = dataUrl;
    });
  };

  const handleCapacitorPhoto = async (source: 'camera' | 'gallery') => {
    setShowPhotoSourceSheet(false);
    try {
      setIsCompressing(true);
      const image = await Camera.getPhoto({
        quality: 80,
        allowEditing: false,
        resultType: CameraResultType.DataUrl,
        source: source === 'camera' ? CameraSource.Camera : CameraSource.Photos,
      });
      if (image.dataUrl) {
        const dataUrl = source === 'camera'
          ? await applyWatermark(image.dataUrl)
          : image.dataUrl;
        const res = await fetch(dataUrl);
        const blob = await res.blob();
        let file: File = new File([blob], `photo_${Date.now()}.jpg`, { type: 'image/jpeg' });
        try { file = await compressImage(file); } catch { /* keep original */ }
        setPhoto(file);
      }
    } catch (error: any) {
      if (error?.message !== 'User cancelled photos app') {
        console.error('Camera error:', error);
      }
    } finally {
      setIsCompressing(false);
    }
  };

  const handlePhotoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setIsCompressing(true);
      let file = e.target.files[0];
      if (file.type.startsWith('image/')) {
        try {
          file = await compressImage(file, 1080, 1080, 0.7);
        } catch (error) {
          console.error("Compression error:", error);
        }
      }
      setPhoto(file);
      setIsCompressing(false);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Handler khusus foto dari kamera browser — terapkan watermark sebelum compress
  const handleCameraPhotoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !file.type.startsWith('image/')) return;
    setIsCompressing(true);
    try {
      const dataUrl = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      const watermarked = await applyWatermark(dataUrl);
      const res = await fetch(watermarked);
      const blob = await res.blob();
      let out: File = new File([blob], `photo_${Date.now()}.jpg`, { type: 'image/jpeg' });
      try { out = await compressImage(out, 1080, 1080, 0.7); } catch { /* keep original */ }
      setPhoto(out);
    } catch {
      // Fallback tanpa watermark jika ada error
      let out = file;
      try { out = await compressImage(file, 1080, 1080, 0.7); } catch {}
      setPhoto(out);
    } finally {
      setIsCompressing(false);
      if (cameraInputRef.current) cameraInputRef.current.value = '';
    }
  };

  const handleCheckIn = async () => {
    if (!photo) {
      showAlert(translate.common.warning, t.validation.photoRequired, 'warning');
      return;
    }
    setIsProcessing(true);
    try {
      let loc: { lat: number; lng: number; address: string; accuracy?: number | null };
      try {
        loc = await fetchCurrentLocation();
      } catch (locErr: any) {
        if (!isBusinessTrip) throw locErr;
        loc = { lat: location?.lat ?? 0, lng: location?.lng ?? 0, address: address || '', accuracy: null };
      }
      const isMockLoc = await checkMockLocation();

      if (!isOnline) {
        const photoRef = await savePhotoForQueue(photo);
        await offlineQueue.add({
          user_id: authSlice.userId || '',
          action_type: 'attendance_checkin',
          payload: {
            latitude: loc.lat, longitude: loc.lng, address: loc.address,
            photo_ref: photoRef, is_business_trip: isBusinessTrip, note: note || '',
            wifi_ssid: wifiInfo?.ssid || null, wifi_bssid: wifiInfo?.bssid || null,
            gps_accuracy: loc.accuracy ?? null,
            is_mock_location: isMockLoc,
          },
        });
        dispatch(setPendingCount(await offlineQueue.getPendingCount()));
        showAlert(translate.common.success, t.offlineSaved || 'Absensi tersimpan offline dan akan dikirim saat terkoneksi.', 'success');
        setPhoto(null); setNote('');
        return;
      }

      const formData = new FormData();
      formData.append('latitude', loc.lat.toString());
      formData.append('longitude', loc.lng.toString());
      formData.append('address', loc.address);
      formData.append('photo', photo);
      formData.append('is_business_trip', String(isBusinessTrip));
      if (note) {
        formData.append('note', note);
      }
      if (wifiInfo?.ssid) formData.append('wifi_ssid', wifiInfo.ssid);
      if (wifiInfo?.bssid) formData.append('wifi_bssid', wifiInfo.bssid);
      if (loc.accuracy != null) formData.append('gps_accuracy', String(loc.accuracy));
      formData.append('is_mock_location', String(isMockLoc));
      const checkInResponse = await axiosInstance.post('/attendances/check-in', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const recurringCount = checkInResponse.data?.recurring_tasks_created ?? 0;
      const successMsg = recurringCount > 0
        ? `${t.successCheckIn}\n${recurringCount} recurring task${recurringCount > 1 ? 's' : ''} telah dibuat otomatis.`
        : t.successCheckIn;
      showAlert(translate.common.success, successMsg, 'success');
      setPhoto(null);
      setNote('');
      fetchTodayStatus();
      setOffset(0);
      fetchHistory(0);
    } catch (error: any) {
      if (error.response) {
        const msg = error.response?.data?.message || translate.common.error;
        showAlert(translate.common.error, msg, 'error');
      } else {
        showAlert(translate.common.warning, error.message, 'warning');
      }
    } finally {
      setIsProcessing(false);
    }
  };

  const handleBreakOut = async () => {
    if (!photo) {
      showAlert(translate.common.warning, t.validation.photoRequired, 'warning');
      return;
    }
    setIsProcessing(true);
    try {
      let loc: { lat: number; lng: number; address: string; accuracy?: number | null };
      try {
        loc = await fetchCurrentLocation();
      } catch (locErr: any) {
        if (!isBusinessTrip) throw locErr;
        loc = { lat: location?.lat ?? 0, lng: location?.lng ?? 0, address: address || '', accuracy: null };
      }

      if (!isOnline) {
        const photoRef = await savePhotoForQueue(photo);
        await offlineQueue.add({
          user_id: authSlice.userId || '',
          action_type: 'attendance_break_out',
          payload: {
            latitude: loc.lat, longitude: loc.lng, address: loc.address,
            photo_ref: photoRef, is_business_trip: isBusinessTrip, note: note || '',
            gps_accuracy: loc.accuracy ?? null,
          },
        });
        dispatch(setPendingCount(await offlineQueue.getPendingCount()));
        showAlert(translate.common.success, t.offlineSaved || 'Tersimpan offline, akan dikirim saat terkoneksi.', 'success');
        setPhoto(null); setNote('');
        return;
      }

      const formData = new FormData();
      formData.append('latitude', loc.lat.toString());
      formData.append('longitude', loc.lng.toString());
      formData.append('address', loc.address);
      formData.append('photo', photo);
      formData.append('is_business_trip', String(isBusinessTrip));
      if (note) formData.append('note', note);
      if (loc.accuracy != null) formData.append('gps_accuracy', String(loc.accuracy));
      await axiosInstance.put('/attendances/break-out', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      showAlert(translate.common.success, t.successBreakOut, 'success');
      setPhoto(null); setNote('');
      fetchTodayStatus(); 
      setOffset(0); 
      fetchHistory(0);
    } catch (error: any) {
      if (error.response) {
        const msg = error.response?.data?.message || translate.common.error;
        showAlert(translate.common.error, msg, 'error');
      } else {
        showAlert(translate.common.warning, error.message, 'warning');
      }
    } finally { setIsProcessing(false); }
  };

  const handleBreakIn = async () => {
    if (!photo) {
      showAlert(translate.common.warning, t.validation.photoRequired, 'warning');
      return;
    }
    setIsProcessing(true);
    try {
      let loc: { lat: number; lng: number; address: string; accuracy?: number | null };
      try {
        loc = await fetchCurrentLocation();
      } catch (locErr: any) {
        if (!isBusinessTrip) throw locErr;
        loc = { lat: location?.lat ?? 0, lng: location?.lng ?? 0, address: address || '', accuracy: null };
      }

      if (!isOnline) {
        const photoRef = await savePhotoForQueue(photo);
        await offlineQueue.add({
          user_id: authSlice.userId || '',
          action_type: 'attendance_break_in',
          payload: {
            latitude: loc.lat, longitude: loc.lng, address: loc.address,
            photo_ref: photoRef, is_business_trip: isBusinessTrip, note: note || '',
            gps_accuracy: loc.accuracy ?? null,
          },
        });
        dispatch(setPendingCount(await offlineQueue.getPendingCount()));
        showAlert(translate.common.success, t.offlineSaved || 'Tersimpan offline, akan dikirim saat terkoneksi.', 'success');
        setPhoto(null); setNote('');
        return;
      }

      const formData = new FormData();
      formData.append('latitude', loc.lat.toString());
      formData.append('longitude', loc.lng.toString());
      formData.append('address', loc.address);
      formData.append('photo', photo);
      formData.append('is_business_trip', String(isBusinessTrip));
      if (note) formData.append('note', note);
      if (loc.accuracy != null) formData.append('gps_accuracy', String(loc.accuracy));
      await axiosInstance.put('/attendances/break-in', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      showAlert(translate.common.success, t.successBreakIn, 'success');
      setPhoto(null); setNote('');
      fetchTodayStatus(); 
      setOffset(0); 
      fetchHistory(0);
    } catch (error: any) {
      if (error.response) {
        const msg = error.response?.data?.message || translate.common.error;
        showAlert(translate.common.error, msg, 'error');
      } else {
        showAlert(translate.common.warning, error.message, 'warning');
      }
    } finally { setIsProcessing(false); }
  };

  const handleCheckOut = async () => {
    if (!photo) {
      showAlert(translate.common.warning, t.validation.photoRequired, 'warning');
      return;
    }
    setIsProcessing(true);
    try {
      let loc: { lat: number; lng: number; address: string; accuracy?: number | null };
      try {
        loc = await fetchCurrentLocation();
      } catch (locErr: any) {
        if (!isBusinessTrip) throw locErr;
        loc = { lat: location?.lat ?? 0, lng: location?.lng ?? 0, address: address || '', accuracy: null };
      }
      const isMockLoc = await checkMockLocation();

      if (!isOnline) {
        const photoRef = await savePhotoForQueue(photo);
        await offlineQueue.add({
          user_id: authSlice.userId || '',
          action_type: 'attendance_checkout',
          payload: {
            latitude: loc.lat, longitude: loc.lng, address: loc.address,
            photo_ref: photoRef, is_business_trip: isBusinessTrip, note: note || '',
            wifi_ssid: wifiInfo?.ssid || null, wifi_bssid: wifiInfo?.bssid || null,
            gps_accuracy: loc.accuracy ?? null,
            is_mock_location: isMockLoc,
          },
        });
        dispatch(setPendingCount(await offlineQueue.getPendingCount()));
        showAlert(translate.common.success, t.offlineSaved || 'Absensi tersimpan offline dan akan dikirim saat terkoneksi.', 'success');
        setPhoto(null); setNote('');
        return;
      }

      const formData = new FormData();
      formData.append('latitude', loc.lat.toString());
      formData.append('longitude', loc.lng.toString());
      formData.append('address', loc.address);
      formData.append('photo', photo);
      formData.append('is_business_trip', String(isBusinessTrip));
      if (note) {
        formData.append('note', note);
      }
      if (wifiInfo?.ssid) formData.append('wifi_ssid', wifiInfo.ssid);
      if (wifiInfo?.bssid) formData.append('wifi_bssid', wifiInfo.bssid);
      if (loc.accuracy != null) formData.append('gps_accuracy', String(loc.accuracy));
      formData.append('is_mock_location', String(isMockLoc));
      await axiosInstance.put('/attendances/check-out', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      showAlert(translate.common.success, t.successCheckOut, 'success');
      setPhoto(null);
      setNote('');
      fetchTodayStatus();
      setOffset(0);
      fetchHistory(0);
    } catch (error: any) {
      if (error.response) {
        const msg = error.response?.data?.message || translate.common.error;
        showAlert(translate.common.error, msg, 'error');
      } else {
        showAlert(translate.common.warning, error.message, 'warning');
      }
    } finally {
      setIsProcessing(false);
    }
  };

  const renderHeader = () => (
    <components.Header
      title={t.title}
      goBack={true}
    />
  );

  // Catatan HR (attendances.hr_entry_type) — tanggal ini dicatat manual oleh HR (mis. Tidak
  // Hadir / Cuti / Perjalanan Dinas), jadi karyawan tidak perlu / tidak bisa absen di tanggal itu.
  const renderHrNotePanel = (att: any) => (
    <div style={{ backgroundColor: '#F5F3FF', border: '1.5px solid #C4B5FD', borderRadius: 12, padding: '10px 12px', display: 'flex', gap: 10, alignItems: 'flex-start' }}>
      <div style={{ width: 8, height: 8, borderRadius: 4, backgroundColor: '#7C3AED', marginTop: 6, flexShrink: 0 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <text.T14 style={{ fontSize: 12, fontWeight: '700', color: '#5B21B6', textTransform: 'uppercase', letterSpacing: 0.4 }}>
          {t.hrNoteTitle} — {att.hr_entry_type}
        </text.T14>
        {att.hr_entered_by_name && (
          <text.T14 style={{ fontSize: 11, color: '#6B7280', marginTop: 2 }}>{t.hrNoteBy} {att.hr_entered_by_name}</text.T14>
        )}
        {att.hr_entry_note && (
          <text.T14 style={{ fontSize: 13, color: '#374151', fontStyle: 'italic', marginTop: 4, wordBreak: 'break-word' }}>
            &ldquo;{att.hr_entry_note}&rdquo;
          </text.T14>
        )}
      </div>
    </div>
  );

  const renderAttendanceCard = () => {
    const hasCheckedIn = !!todayStatus?.check_in_time;
    const hasBrokeOut = !!todayStatus?.break_out_time;
    const hasBrokeIn = !!todayStatus?.break_in_time;
    const hasCheckedOut = !!todayStatus?.check_out_time;
    const isCompleted = hasCheckedIn && hasCheckedOut;

    const timeString = currentTime.toLocaleTimeString(language === 'id' ? 'id-ID' : 'en-US', {
      hour: '2-digit', minute: '2-digit', second: '2-digit'
    });
    const dateString = currentTime.toLocaleDateString(language === 'id' ? 'id-ID' : 'en-US', {
      weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
    });
    
    const isButtonDisabled = isProcessing || isFetchingLocation || isCompressing || (!isBusinessTrip && !!locationError);

    // Checkout hanya bisa dilakukan setelah jam akhir shift
    const shiftEndHHMM = todayStatus?.work_end_time
      ? String(todayStatus.work_end_time).substring(0, 5)
      : null;
    const currentHHMM = `${String(currentTime.getHours()).padStart(2, '0')}:${String(currentTime.getMinutes()).padStart(2, '0')}`;

    // Deteksi jika status yang ditampilkan adalah record hari sebelumnya (lupa checkout)
    const todayLocalDate = `${currentTime.getFullYear()}-${String(currentTime.getMonth() + 1).padStart(2, '0')}-${String(currentTime.getDate()).padStart(2, '0')}`;
    const isPreviousDayRecord = !!todayStatus?.date && String(todayStatus.date).substring(0, 10) < todayLocalDate;

    // Break tidak mandatory — jika user sedang on-break (break_out ada, break_in belum),
    // jangan batasi checkout berdasarkan jam sehingga user bisa checkout tanpa harus end break dulu.
    // Ini mencegah record terbuka (check_out NULL) yang memblokir check-in hari berikutnya.
    const isOnBreak = hasBrokeOut && !hasBrokeIn;
    const isBeforeShiftEnd = !isPreviousDayRecord && !isOnBreak && !!shiftEndHHMM && currentHHMM < shiftEndHHMM;
    const isCheckoutDisabled = isButtonDisabled || isBeforeShiftEnd;

    // Hari ini sudah dicatat HR → tidak ada aksi absensi (backend juga menolak check-in)
    if (todayStatus?.hr_entry_type && !hasCheckedIn && !isPreviousDayRecord) {
      return (
        <div style={{
          backgroundColor: '#FFFFFF', borderRadius: 24, padding: 16, marginBottom: 24,
          boxShadow: '0 10px 30px rgba(0,0,0,0.05)', border: '1px solid #F3F4F6',
          display: 'flex', flexDirection: 'column', gap: 12,
        }}>
          <div>
            <text.T16 style={{ color: '#6B7280', marginBottom: 4, fontWeight: '500' }}>{dateString}</text.T16>
            <text.H1 style={{ fontSize: 32, color: theme.colors.mainDark, letterSpacing: 1 }}>{timeString}</text.H1>
          </div>
          {renderHrNotePanel(todayStatus)}
          <text.T14 style={{ fontSize: 13, color: '#6B7280' }}>{t.hrNoteTodayDesc}</text.T14>
        </div>
      );
    }

    return (
      <div style={{
        backgroundColor: '#FFFFFF',
        borderRadius: 24,
        padding: 16,
        boxShadow: '0 10px 30px rgba(0,0,0,0.05)',
        border: '1px solid #F3F4F6',
        marginBottom: 24,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        position: 'relative'
      }}>
        {/* Background Circles Layer */}
        <div style={{ position: 'absolute', inset: 0, borderRadius: 24, overflow: 'hidden', pointerEvents: 'none' }}>
          <div style={{ position: 'absolute', top: -50, right: -50, width: 150, height: 150, borderRadius: 75, backgroundColor: theme.colors.mainColor, opacity: 0.05 }} />
          <div style={{ position: 'absolute', bottom: -30, left: -30, width: 100, height: 100, borderRadius: 50, backgroundColor: '#10B981', opacity: 0.05 }} />
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', zIndex: 1, marginBottom: 16 }}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
            <text.T16 style={{ color: '#6B7280', marginBottom: 4, fontWeight: '500' }}>{dateString}</text.T16>
            <text.H1 style={{ fontSize: 32, color: theme.colors.mainDark, letterSpacing: 1 }}>{timeString}</text.H1>
          </div>

          {!isCompleted && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              {!photo ? (
                <div
                  onClick={() => {
                    if (isCompressing) return;
                    setShowPhotoSourceSheet(true);
                  }}
                  style={{
                    width: 72, height: 72, borderRadius: 16,
                    backgroundColor: '#F9FAFB', border: '2px dashed #D1D5DB',
                    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                    cursor: isCompressing ? 'not-allowed' : 'pointer', transition: 'all 0.3s'
                  }}
                >
                  {isCompressing ? (
                    <>
                      <div style={{ width: 24, height: 24, borderRadius: 12, border: `2px solid ${theme.colors.mainColor}`, borderTopColor: 'transparent', animation: 'spin 1s linear infinite', marginBottom: 4 }} />
                      <text.T14 style={{ color: theme.colors.mainColor, fontSize: 10, textAlign: 'center', fontWeight: 'bold' }}>{translate.common?.processing || 'Memproses...'}</text.T14>
                    </>
                  ) : (
                    <>
                      <CameraIcon size={24} color="#9CA3AF" />
                      <text.T14 style={{ color: '#6B7280', fontSize: 10, marginTop: 4, textAlign: 'center' }}>{t.takePhoto}</text.T14>
                    </>
                  )}
                </div>
              ) : (
                <div style={{ position: 'relative', width: 72, height: 72 }}>
                  <img 
                    src={photoPreviewUrl!} 
                    alt="Selfie" 
                    style={{ width: 72, height: 72, borderRadius: 16, objectFit: 'cover', border: `2px solid ${theme.colors.mainColor}` }} 
                  />
                  <div 
                    onClick={() => setPhoto(null)}
                    style={{
                      position: 'absolute', top: -8, right: -8,
                      backgroundColor: '#EF4444', width: 24, height: 24, borderRadius: 12,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      cursor: 'pointer', border: '2px solid white'
                    }}
                  >
                    <CloseIcon size={14} color="white" />
                  </div>
                </div>
              )}
              <input
                type="file"
                accept="image/*"
                ref={fileInputRef}
                style={{ display: 'none' }}
                onChange={handlePhotoChange}
              />
              <input
                type="file"
                accept="image/*"
                capture="environment"
                ref={cameraInputRef}
                style={{ display: 'none' }}
                onChange={handleCameraPhotoChange}
              />
            </div>
          )}
        </div>

        {isCompleted ? (
          <div style={{
            backgroundColor: '#ECFDF5', padding: '14px 20px', borderRadius: 16,
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, width: '100%', zIndex: 1, border: '1px solid #D1FAE5'
          }}>
            <CheckCircleIcon size={42} color="#10B981" />
            <text.H5 style={{ color: '#065F46', textAlign: 'center' }}>{t.alerts.alreadyCompleted}</text.H5>
          </div>
        ) : (
          <div style={{ width: '100%', zIndex: 1 }}>

            {/* Nearest POI indicator */}
            {nearestPoiInfo && (
              <div style={{
                marginBottom: 10, padding: '8px 12px', borderRadius: 10,
                backgroundColor: nearestPoiInfo.inRange ? '#ECFDF5' : '#F9FAFB',
                border: `1px solid ${nearestPoiInfo.inRange ? '#A7F3D0' : '#E5E7EB'}`,
                display: 'flex', alignItems: 'center', gap: 6,
              }}>
                <LocationIcon size={15} color={nearestPoiInfo.inRange ? '#10B981' : '#9CA3AF'} />
                <text.T14 style={{ fontSize: 12, color: nearestPoiInfo.inRange ? '#065F46' : '#6B7280', flex: 1 }}>
                  {nearestPoiInfo.name}
                </text.T14>
                {!nearestPoiInfo.inRange && (
                  <text.T14 style={{ fontSize: 11, color: '#9CA3AF' }}>{nearestPoiInfo.distance}m</text.T14>
                )}
                {nearestPoiInfo.inRange && (
                  <CheckCircleIcon size={14} color="#10B981" />
                )}
              </div>
            )}

            {geofenceWarning && !isBusinessTrip && (
              <div style={{
                marginBottom: 12, padding: '10px 14px', borderRadius: 12,
                backgroundColor: '#FFFBEB',
                border: `1px solid #FBBF24`,
                width: '100%', textAlign: 'center',
                display: 'flex', alignItems: 'center', gap: 8
              }}>
                <WarningIcon size={20} color="#D97706" />
                <text.T14 style={{ color: '#B45309', fontSize: 12, display: 'block', textAlign: 'left' }}>
                  {geofenceWarning}
                </text.T14>
              </div>
            )}

            {isFetchingLocation && (
              <text.T14 style={{ textAlign: 'center', display: 'block', marginBottom: 12, color: '#9CA3AF' }}>
                {t.fetchingLocation}
              </text.T14>
            )}

            {!isFetchingLocation && locationError && (
              <text.T14 style={{ textAlign: 'center', display: 'block', marginBottom: 12, color: '#EF4444' }}>
                {t.alerts.locationNotAcquired}
              </text.T14>
            )}

            {/* WiFi info (hanya tampil di native Capacitor) */}
            {isNative && (
              <div style={{
                marginBottom: 10, padding: '8px 12px', borderRadius: 10,
                backgroundColor: wifiInfo ? '#EFF6FF' : '#F9FAFB',
                border: `1px solid ${wifiInfo ? '#BFDBFE' : '#E5E7EB'}`,
                display: 'flex', alignItems: 'center', gap: 8,
              }}>
                {wifiInfo
                  ? <WifiIcon size={16} color="#3B82F6" />
                  : <WifiOffIcon size={16} color="#9CA3AF" />
                }
                <div style={{ flex: 1, minWidth: 0 }}>
                  <text.T14 style={{ fontSize: 12, color: wifiInfo ? '#1D4ED8' : '#6B7280', display: 'block' }}>
                    {wifiInfo ? wifiInfo.ssid : 'Not Connected'}
                  </text.T14>
                  {wifiInfo && (
                    <text.T14 style={{ fontSize: 10, color: '#93C5FD', display: 'block' }}>
                      {wifiInfo.bssid}
                    </text.T14>
                  )}
                </div>
              </div>
            )}

            {/* Perjalanan Dinas Checkbox */}
            <div
              onClick={() => setIsBusinessTrip(!isBusinessTrip)}
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '8px 12px', backgroundColor: isBusinessTrip ? '#EFF6FF' : '#F9FAFB', borderRadius: 10,
                border: isBusinessTrip ? `1px solid ${theme.colors.mainColor}` : '1px solid #E5E7EB',
                cursor: 'pointer', marginBottom: 12, width: '100%', boxSizing: 'border-box'
              }}
            >
              {isBusinessTrip ? <CheckBoxIcon size={20} color={theme.colors.mainColor} /> : <CheckBoxOutlineBlankIcon size={20} color="#9CA3AF" />}
              <text.T14 style={{fontSize: 13, color: isBusinessTrip ? theme.colors.mainColor : theme.colors.mainDark, fontWeight: isBusinessTrip ? '600' : 'normal'}}>
                 {t.businessTrip || 'Perjalanan Dinas'}
              </text.T14>
            </div>

            {/* Note Input */}
            <div style={{ width: '100%', marginBottom: 12, flexShrink: 0 }}>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder={t.notePlaceholder || "Catatan (Opsional)..."}
                style={{
                  width: '100%',
                  padding: '10px 14px',
                  borderRadius: 12,
                  border: '1px solid #E5E7EB',
                  backgroundColor: '#F9FAFB',
                  fontSize: 14,
                  resize: 'none',
                  outline: 'none',
                  minHeight: 60,
                  boxSizing: 'border-box'
                }}
              />
            </div>

            {/* Warning banner: ada record hari sebelumnya yang belum checkout */}
            {isPreviousDayRecord && hasCheckedIn && !hasCheckedOut && (() => {
              const recDate = todayStatus?.date ? new Date(String(todayStatus.date).substring(0, 10) + 'T00:00:00') : null;
              const recDateStr = recDate
                ? recDate.toLocaleDateString(language === 'id' ? 'id-ID' : 'en-US', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
                : '';
              return (
                <div style={{
                  width: '100%', backgroundColor: '#FEF3C7', border: '1px solid #F59E0B',
                  borderRadius: 12, padding: '10px 14px', marginBottom: 8, display: 'flex',
                  alignItems: 'flex-start', gap: 10, boxSizing: 'border-box'
                }}>
                  <WarningIcon size={20} color="#D97706" style={{ flexShrink: 0, marginTop: 1 }} />
                  <div>
                    <div style={{ fontSize: 13, fontWeight: '600', color: '#92400E', marginBottom: 2 }}>
                      {(t as any)?.prevDayWarning || 'Ada absensi belum selesai pada'} {recDateStr}
                    </div>
                    <div style={{ fontSize: 12, color: '#B45309' }}>
                      {(t as any)?.prevDayCheckoutFirst || 'Silakan checkout terlebih dahulu untuk dapat check in hari ini.'}
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* Button Logic Berdasarkan Status */}
            <div style={{ width: '100%', display: 'flex', gap: 10, flexShrink: 0 }}>
              {/* Record hari sebelumnya: langsung tampilkan tombol checkout saja */}
              {isPreviousDayRecord && hasCheckedIn && !hasCheckedOut && (
                <button
                  onClick={handleCheckOut}
                  disabled={isCheckoutDisabled}
                  style={{ flex: 1, padding: 12, borderRadius: 14, backgroundColor: isCheckoutDisabled ? '#D1D5DB' : '#F59E0B', color: 'white', border: 'none', fontSize: 15, fontWeight: 'bold', cursor: isCheckoutDisabled ? 'not-allowed' : 'pointer' }}
                >
                  {isProcessing ? translate.common.processing : t.checkOut}
                </button>
              )}

              {/* Record hari ini: tampilkan alur normal */}
              {!isPreviousDayRecord && (
                <>
                  {!hasCheckedIn && (
                    <button
                      onClick={handleCheckIn}
                      disabled={isButtonDisabled}
                      style={{ flex: 1, padding: 12, borderRadius: 14, backgroundColor: isButtonDisabled ? '#D1D5DB' : theme.colors.mainColor, color: 'white', border: 'none', fontSize: 15, fontWeight: 'bold', cursor: isButtonDisabled ? 'not-allowed' : 'pointer' }}
                    >
                      {isProcessing ? translate.common.processing : t.checkIn}
                    </button>
                  )}

                  {hasCheckedIn && !hasBrokeOut && !hasCheckedOut && (
                    <>
                      <button
                        onClick={handleBreakOut}
                        disabled={isButtonDisabled}
                        style={{ flex: 1, padding: 12, borderRadius: 14, backgroundColor: isButtonDisabled ? '#D1D5DB' : '#8B5CF6', color: 'white', border: 'none', fontSize: 15, fontWeight: 'bold', cursor: isButtonDisabled ? 'not-allowed' : 'pointer' }}
                      >
                        {isProcessing ? translate.common.processing : t.breakOut}
                      </button>
                      <button
                        onClick={handleCheckOut}
                        disabled={isCheckoutDisabled}
                        style={{ flex: 1, padding: 12, borderRadius: 14, backgroundColor: isCheckoutDisabled ? '#D1D5DB' : '#F59E0B', color: 'white', border: 'none', fontSize: 15, fontWeight: 'bold', cursor: isCheckoutDisabled ? 'not-allowed' : 'pointer' }}
                      >
                        {isProcessing ? translate.common.processing : t.checkOut}
                      </button>
                    </>
                  )}

                  {hasBrokeOut && !hasBrokeIn && !hasCheckedOut && (
                    <>
                      <button
                        onClick={handleBreakIn}
                        disabled={isButtonDisabled}
                        style={{ flex: 1, padding: 12, borderRadius: 14, backgroundColor: isButtonDisabled ? '#D1D5DB' : '#10B981', color: 'white', border: 'none', fontSize: 15, fontWeight: 'bold', cursor: isButtonDisabled ? 'not-allowed' : 'pointer' }}
                      >
                        {isProcessing ? translate.common.processing : t.breakIn}
                      </button>
                      <button
                        onClick={handleCheckOut}
                        disabled={isCheckoutDisabled}
                        style={{ flex: 1, padding: 12, borderRadius: 14, backgroundColor: isCheckoutDisabled ? '#D1D5DB' : '#F59E0B', color: 'white', border: 'none', fontSize: 15, fontWeight: 'bold', cursor: isCheckoutDisabled ? 'not-allowed' : 'pointer' }}
                      >
                        {isProcessing ? translate.common.processing : t.checkOut}
                      </button>
                    </>
                  )}

                  {hasBrokeIn && !hasCheckedOut && (
                    <button
                      onClick={handleCheckOut}
                      disabled={isCheckoutDisabled}
                      style={{ flex: 1, padding: 12, borderRadius: 14, backgroundColor: isCheckoutDisabled ? '#D1D5DB' : '#F59E0B', color: 'white', border: 'none', fontSize: 15, fontWeight: 'bold', cursor: isCheckoutDisabled ? 'not-allowed' : 'pointer' }}
                    >
                      {isProcessing ? translate.common.processing : t.checkOut}
                    </button>
                  )}
                </>
              )}
            </div>

            {/* Info jam checkout tersedia */}
            {isBeforeShiftEnd && shiftEndHHMM && (
              <text.T14 style={{ textAlign: 'center', color: '#6B7280', fontSize: 11, marginTop: 6, display: 'block' }}>
                {(t as any).checkOutAvailableAt?.replace('{time}', shiftEndHHMM)
                  || `Check out tersedia mulai pukul ${shiftEndHHMM}`}
              </text.T14>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderFilters = () => {
    const inputStyle: React.CSSProperties = {
      width: '100%', height: 44, padding: '0 10px', borderRadius: 10,
      border: '1px solid #FFEFE6', backgroundColor: theme.colors.white,
      outline: 'none', fontSize: 13, fontFamily: 'inherit',
      color: theme.colors.mainDark, cursor: 'pointer', boxSizing: 'border-box',
      minWidth: 0,
    };

    return (
      <div style={{
        position: 'sticky', top: 0, backgroundColor: '#FFFFFF',
        zIndex: 10, paddingBottom: 12, paddingTop: 4,
      }}>
        <text.H5 style={{ marginBottom: 12 }}>{t.history}</text.H5>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{
            width: 44, height: 44, backgroundColor: theme.colors.white, borderRadius: 10,
            display: 'flex', justifyContent: 'center', alignItems: 'center',
            border: '1px solid #FFEFE6', flexShrink: 0
          }}>
            <FilterListIcon size={24} color={theme.colors.mainDark} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} style={inputStyle} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} style={inputStyle} />
          </div>
        </div>
      </div>
    );
  };

  const formatTime = (timeString: string) => {
    if (!timeString) return '--:--';
    return new Date(timeString).toLocaleTimeString(language === 'id' ? 'id-ID' : 'en-US', { hour: '2-digit', minute: '2-digit' });
  };

  const renderTimeBlock = (label: string, time: string, note: string, address: string, photo: string, color: string) => {
    if (!time) return null; // Jika waktu belum ada, kembalikan null (bisa dihapus jika tetap ingin menampilkan kotak kosong)
    return (
      <div style={{ backgroundColor: '#F9FAFB', padding: 12, borderRadius: 10, display: 'flex', gap: 12, alignItems: 'flex-start' }}>
        <div style={{color: color, marginTop: 2}}><AccessTimeIcon size={20}/></div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <text.T14 style={{ color: '#6B7280', fontSize: 11, marginBottom: 2 }}>{label}</text.T14>
          <text.H5 style={{ fontSize: 14 }}>{formatTime(time)}</text.H5>
          {address && (
            <div style={{ display: 'flex', gap: 4, marginTop: 4, alignItems: 'flex-start' }}>
               <LocationIcon size={14} color="#9CA3AF" style={{ marginTop: 1, flexShrink: 0 }} />
               <text.T14 style={{ fontSize: 11, color: '#9CA3AF', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                 {address}
               </text.T14>
            </div>
          )}
          {note && (
            <text.T14 style={{ fontSize: 12, color: '#6B7280', marginTop: 4, fontStyle: 'italic', wordBreak: 'break-word', display: 'block' }}>
              "{note}"
            </text.T14>
          )}
        </div>
        {photo && (
          <div style={{ flexShrink: 0, cursor: 'pointer', width: 50, height: 50 }} onClick={() => setPreviewImage(`${imageBaseUrl}${photo}`)}>
            <img
              src={`${imageBaseUrl}${photo}`}
              alt="proof"
              loading="lazy"
              width={50}
              height={50}
              style={{ width: 50, height: 50, borderRadius: 8, objectFit: 'cover', border: '1px solid #E5E7EB', display: 'block' }}
              onError={(e) => {
                const el = e.currentTarget;
                if (!el.dataset.retried) {
                  el.dataset.retried = '1';
                  // Re-assign src untuk retry sekali jika load diinterrupt re-render
                  el.src = `${imageBaseUrl}${photo}`;
                }
              }}
            />
          </div>
        )}
      </div>
    );
  };

  const handleCancelCorrection = (id: number) => {
    showConfirm(
      tCorr.cancelConfirm || 'Apakah Anda yakin ingin membatalkan permintaan koreksi ini?',
      async () => {
        try {
          await axiosInstance.delete(`/attendance-corrections/${id}`);
          fetchCorrections();
        } catch (err: any) {
          const msg = err.response?.data?.message || translate.common.error;
          showAlert(translate.common.error, msg, 'error');
        }
      }
    );
  };

  const corrStatusColor = (status: string) =>
    status === 'approved' ? '#10B981' : status === 'rejected' ? '#EF4444' : '#F59E0B';
  const corrStatusBg = (status: string) =>
    status === 'approved' ? '#ECFDF5' : status === 'rejected' ? '#FEF2F2' : '#FFFBEB';
  const corrStatusLabel = (status: string) =>
    status === 'approved' ? (tCorr.approved || 'Disetujui') : status === 'rejected' ? (tCorr.rejected || 'Ditolak') : (tCorr.pending || 'Menunggu');

  const renderCorrectionPanel = (corr: any) => (
    <div style={{ marginTop: 12, padding: 12, borderRadius: 10, backgroundColor: corrStatusBg(corr.status), border: `1px solid ${corrStatusColor(corr.status)}44` }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
        <text.T14 style={{ fontSize: 12, fontWeight: '700', color: corrStatusColor(corr.status) }}>{tCorr.sectionTitle || 'Permintaan Koreksi'}</text.T14>
        <span style={{ padding: '2px 8px', borderRadius: 10, backgroundColor: corrStatusColor(corr.status), color: 'white', fontSize: 10, fontWeight: 'bold' }}>
          {corrStatusLabel(corr.status)}
        </span>
      </div>
      {corr.check_in_time && <text.T14 style={{ fontSize: 12, color: '#374151', display: 'block' }}>{t.checkInTime}: {formatTime(corr.check_in_time)}</text.T14>}
      {corr.check_out_time && <text.T14 style={{ fontSize: 12, color: '#374151', display: 'block' }}>{t.checkOutTime}: {formatTime(corr.check_out_time)}</text.T14>}
      {corr.break_out_time && <text.T14 style={{ fontSize: 12, color: '#374151', display: 'block' }}>{t.breakOutTime}: {formatTime(corr.break_out_time)}</text.T14>}
      {corr.break_in_time && <text.T14 style={{ fontSize: 12, color: '#374151', display: 'block' }}>{t.breakInTime}: {formatTime(corr.break_in_time)}</text.T14>}
      {corr.reason && <text.T14 style={{ fontSize: 12, color: '#6B7280', marginTop: 4, fontStyle: 'italic', display: 'block' }}>"{corr.reason}"</text.T14>}
      {corr.status === 'rejected' && corr.reject_reason && (
        <text.T14 style={{ fontSize: 12, color: '#EF4444', marginTop: 4, display: 'block' }}>
          {tCorr.rejectedReason || 'Alasan Penolakan'}: {corr.reject_reason}
        </text.T14>
      )}
      {corr.status === 'pending' && (
        <div style={{ marginTop: 10, display: 'flex', justifyContent: 'flex-end' }}>
          <button
            onClick={() => handleCancelCorrection(corr.id)}
            style={{
              padding: '4px 12px', borderRadius: 8,
              border: '1px solid #EF4444', backgroundColor: 'transparent',
              color: '#EF4444', fontSize: 11, fontWeight: '600',
              cursor: 'pointer',
            }}
          >
            {tCorr.cancelRequest || 'Batalkan Pengajuan'}
          </button>
        </div>
      )}
    </div>
  );

  const renderList = () => {
    if (isLoading) {
      return <text.T16 style={{ textAlign: 'center', marginTop: 40 }}>{translate.common.loading}</text.T16>;
    }

    const normalizeDate = (d: string) => d?.split('T')[0] ?? '';
    const corrByDate: Record<string, any> = {};
    corrections.forEach((c: any) => {
      const key = normalizeDate(c.date);
      if (!corrByDate[key]) corrByDate[key] = c; // keep most recent (API sorted DESC)
    });
    const attendanceDates = new Set(attendances.map((a: any) => normalizeDate(a.date)));
    // Attendances are loaded newest-first in pages of 10. A correction for a date
    // older than the oldest loaded attendance may still have an attendance record
    // that hasn't been paginated in yet — don't show it as orphan until we've
    // loaded far enough to confirm there's truly no matching attendance record.
    const oldestLoadedDate = attendances.length > 0
      ? normalizeDate(attendances[attendances.length - 1].date)
      : null;
    const orphanCorrections = corrections.filter((c: any) => {
      const corrDate = normalizeDate(c.date);
      if (attendanceDates.has(corrDate)) return false;
      if (!hasMore) return true; // all pages loaded — confirmed no attendance
      if (!oldestLoadedDate) return false; // nothing loaded yet
      // Only treat as orphan if the date falls within the already-loaded range
      return corrDate >= oldestLoadedDate;
    });

    if (attendances.length === 0 && orphanCorrections.length === 0) {
      return (
        <div style={{ textAlign: 'center', marginTop: 40, opacity: 0.5 }}>
          <text.T16 style={{ marginTop: 10 }}>{t.noData}</text.T16>
        </div>
      );
    }

    return (
      <InfiniteScroll
        dataLength={attendances.length}
        next={() => setOffset(prev => prev + 10)}
        hasMore={hasMore}
        loader={
          <div style={{ textAlign: 'center', padding: '10px 0' }}>
            <text.T14 style={{ color: '#6B7280', textAlign: 'center' }}>{translate.leaveHistory?.loadingMore || 'Memuat data lainnya...'}</text.T14>
          </div>
        }
        endMessage={
          <div style={{ textAlign: 'center', padding: '10px 0', marginBottom: 20 }}>
            <text.T14 style={{ color: '#6B7280', textAlign: 'center'}}>{translate.leaveHistory?.allDataLoaded || 'Semua data telah dimuat'}</text.T14>
          </div>
        }
        scrollableTarget="scrollableDiv"
        style={{ overflow: 'visible' }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {/* Standalone correction cards for dates with no attendance record */}
          {orphanCorrections.map((corr: any) => {
            const corrDateLabel = new Date(corr.date).toLocaleDateString(language === 'id' ? 'id-ID' : 'en-US', { weekday: 'short', day: 'numeric', month: 'long', year: 'numeric' });
            return (
              <div
                key={`corr-${corr.id}`}
                style={{ backgroundColor: 'white', padding: 16, borderRadius: 16, border: '1px solid #F3F4F6', boxShadow: '0 4px 15px rgba(0,0,0,0.02)' }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                  <div style={{ width: 36, height: 36, borderRadius: 10, backgroundColor: '#FEF3C7', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <EventNoteIcon size={20} color="#D97706" />
                  </div>
                  <text.H5 style={{ color: theme.colors.mainDark, fontSize: 15 }}>{corrDateLabel}</text.H5>
                </div>
                {renderCorrectionPanel(corr)}
              </div>
            );
          })}
          {attendances.map((att) => {
            const dateLabel = new Date(att.date).toLocaleDateString(language === 'id' ? 'id-ID' : 'en-US', { weekday: 'short', day: 'numeric', month: 'long', year: 'numeric' });
            const matchedCorr = corrByDate[normalizeDate(att.date)];
            const isVoided = !!att.voided_by_leave_id;
            return (
              <div
                key={att.id}
                style={{
                  backgroundColor: isVoided ? '#F9FAFB' : 'white', padding: 16, borderRadius: 16,
                  border: `1px solid ${isVoided ? '#E5E7EB' : '#F3F4F6'}`,
                  boxShadow: '0 4px 15px rgba(0,0,0,0.02)',
                  opacity: isVoided ? 0.75 : 1,
                }}
              >
                <div style={{ ...utils.rowCenterSpcBtw(), marginBottom: 12, borderBottom: '1px solid #F3F4F6', paddingBottom: 12 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{ width: 36, height: 36, borderRadius: 10, backgroundColor: '#EFF6FF', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <EventNoteIcon size={20} color="#3B82F6" />
                    </div>
                    <div>
                      <text.H5 style={{ color: theme.colors.mainDark, fontSize: 15 }}>{dateLabel}</text.H5>
                      {att.is_business_trip && (
                        <div style={{
                          padding: '2px 6px', borderRadius: 12, fontSize: 10, fontWeight: 'bold', display: 'inline-block', marginTop: 4,
                          backgroundColor: att.approval_status === 'approved' ? '#D1FAE5' : (att.approval_status === 'rejected' ? '#FEE2E2' : '#FEF3C7'),
                          color: att.approval_status === 'approved' ? '#10B981' : (att.approval_status === 'rejected' ? '#EF4444' : '#D97706')
                        }}>
                          {att.approval_status === 'approved' ? (t.businessTripApproved || 'Dinas Disetujui') : (att.approval_status === 'rejected' ? (t.businessTripRejected || 'Dinas Ditolak') : (t.businessTripPending || 'Dinas Menunggu Approval'))}
                        </div>
                      )}
                    </div>
                  </div>
                  <div style={{
                    padding: '2px 8px', borderRadius: 20, fontSize: 11, fontWeight: 'bold',
                    backgroundColor: '#D1FAE5', color: '#10B981'
                  }}>
                    {att.status || t.present}
                  </div>
                </div>

                {isVoided && (
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    backgroundColor: '#FEF3C7', border: '1px solid #FCD34D',
                    borderRadius: 10, padding: '8px 12px', marginBottom: 4,
                  }}>
                    <WarningIcon size={16} color="#D97706" />
                    <text.T14 style={{ fontSize: 12, color: '#92400E' }}>
                      {t.cancelledByLeave}
                    </text.T14>
                  </div>
                )}

                {att.hr_entry_type && (
                  <div style={{ marginBottom: att.check_in_time ? 10 : 0 }}>{renderHrNotePanel(att)}</div>
                )}

                {!(att.hr_entry_type && !att.check_in_time) && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {renderTimeBlock(t.checkInTime, att.check_in_time, att.check_in_note, att.check_in_address, att.check_in_photo, theme.colors.mainColor)}
                  {!att.break_out_time && att.check_out_time ? (
                    <div style={{ backgroundColor: '#F9FAFB', padding: 12, borderRadius: 10, display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                      <div style={{color: '#8B5CF6', marginTop: 2}}><AccessTimeIcon size={20}/></div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <text.T14 style={{ color: '#6B7280', fontSize: 11, marginBottom: 2 }}>{t.breakOutTime}</text.T14>
                        <text.H5 style={{ fontSize: 14, color: '#EF4444' }}>{t.noBreakOut}</text.H5>
                      </div>
                    </div>
                  ) : (
                    renderTimeBlock(t.breakOutTime, att.break_out_time, att.break_out_note, att.break_out_address, att.break_out_photo, '#8B5CF6')
                  )}
                  {!att.break_in_time && att.check_out_time ? (
                    <div style={{ backgroundColor: '#F9FAFB', padding: 12, borderRadius: 10, display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                      <div style={{color: '#10B981', marginTop: 2}}><AccessTimeIcon size={20}/></div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <text.T14 style={{ color: '#6B7280', fontSize: 11, marginBottom: 2 }}>{t.breakInTime}</text.T14>
                        <text.H5 style={{ fontSize: 14, color: '#EF4444' }}>{t.noBreakIn}</text.H5>
                      </div>
                    </div>
                  ) : (
                    renderTimeBlock(t.breakInTime, att.break_in_time, att.break_in_note, att.break_in_address, att.break_in_photo, '#10B981')
                  )}
                  {renderTimeBlock(t.checkOutTime, att.check_out_time, att.check_out_note, att.check_out_address, att.check_out_photo, '#F59E0B')}
                </div>
                )}

                {matchedCorr && renderCorrectionPanel(matchedCorr)}
              </div>
            );
          })}
        </div>
      </InfiniteScroll>
    );
  };

  const renderCorrectionModal = () => {
    if (!showCorrectionModal) return null;

    const inputStyle: React.CSSProperties = {
      width: '100%', height: 44, padding: '0 14px', borderRadius: 10,
      border: '1px solid #E5E7EB', backgroundColor: '#F9FAFB',
      fontSize: 14, fontFamily: 'inherit', outline: 'none',
      color: theme.colors.mainDark, boxSizing: 'border-box',
    };
    const labelStyle: React.CSSProperties = {
      fontSize: 12, color: '#6B7280', fontWeight: '600',
      display: 'block', marginBottom: 6,
    };

    return (
      <div style={{ position: 'fixed', inset: 0, zIndex: 3000, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
        <div style={{
          backgroundColor: 'white', borderRadius: '20px 20px 0 0',
          width: '100%', maxWidth: 650, maxHeight: '88vh', overflowY: 'auto',
          padding: 20, paddingBottom: 32, boxSizing: 'border-box',
        }}>
          {/* Header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
            <text.H5>{tCorr.title || 'Ajukan Koreksi Absensi'}</text.H5>
            <button
              onClick={() => setShowCorrectionModal(false)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, display: 'flex' }}
            >
              <CloseIcon size={22} color="#6B7280" />
            </button>
          </div>

          {/* Date (required) */}
          <div style={{ marginBottom: 16 }}>
            <span style={labelStyle}>{tCorr.dateLabel || 'Tanggal'} *</span>
            <input
              type="date"
              value={corrDate}
              max={new Date().toISOString().split('T')[0]}
              onChange={(e) => setCorrDate(e.target.value)}
              style={inputStyle}
            />
          </div>

          {/* Time fields - 2 column grid */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
            <div>
              <span style={labelStyle}>{tCorr.checkInLabel || 'Waktu Masuk'} <span style={{ color: '#9CA3AF', fontWeight: 400 }}>({tCorr.optional || 'Opsional'})</span></span>
              <input type="time" value={corrCheckIn} onChange={(e) => setCorrCheckIn(e.target.value)} style={inputStyle} />
            </div>
            <div>
              <span style={labelStyle}>{tCorr.checkOutLabel || 'Waktu Keluar'} <span style={{ color: '#9CA3AF', fontWeight: 400 }}>({tCorr.optional || 'Opsional'})</span></span>
              <input type="time" value={corrCheckOut} onChange={(e) => setCorrCheckOut(e.target.value)} style={inputStyle} />
              <span style={{ fontSize: 11, color: '#9CA3AF', marginTop: 3, display: 'block', lineHeight: 1.4 }}>
                {(tCorr as any).checkOutHint || 'Jika pulang lewat tengah malam, masukkan jam biasa — sistem menyesuaikan otomatis'}
              </span>
            </div>
            <div>
              <span style={labelStyle}>{tCorr.breakOutLabel || 'Keluar Istirahat'} <span style={{ color: '#9CA3AF', fontWeight: 400 }}>({tCorr.optional || 'Opsional'})</span></span>
              <input type="time" value={corrBreakOut} onChange={(e) => setCorrBreakOut(e.target.value)} style={inputStyle} />
            </div>
            <div>
              <span style={labelStyle}>{tCorr.breakInLabel || 'Masuk Istirahat'} <span style={{ color: '#9CA3AF', fontWeight: 400 }}>({tCorr.optional || 'Opsional'})</span></span>
              <input type="time" value={corrBreakIn} onChange={(e) => setCorrBreakIn(e.target.value)} style={inputStyle} />
            </div>
          </div>

          {/* Reason (required) */}
          <div style={{ marginBottom: 16 }}>
            <span style={labelStyle}>{tCorr.reasonLabel || 'Alasan'} *</span>
            <textarea
              value={corrReason}
              onChange={(e) => setCorrReason(e.target.value)}
              placeholder={tCorr.reasonPlaceholder || 'Jelaskan alasan koreksi absensi...'}
              style={{
                ...inputStyle, height: 'auto', minHeight: 80, padding: '10px 14px',
                resize: 'none', lineHeight: 1.5,
              }}
            />
          </div>

          {/* Evidence (optional) */}
          <div style={{ marginBottom: 24 }}>
            <span style={labelStyle}>{tCorr.evidenceLabel || 'Bukti Foto'} <span style={{ color: '#9CA3AF', fontWeight: 400 }}>({tCorr.optional || 'Opsional'})</span></span>
            <input
              type="file"
              accept="image/*"
              ref={corrEvidenceRef}
              style={{ display: 'none' }}
              onChange={handleCorrEvidenceChange}
            />
            {corrEvidencePreview ? (
              <div style={{ position: 'relative', display: 'inline-block' }}>
                <img src={corrEvidencePreview} alt="evidence" style={{ width: 80, height: 80, borderRadius: 10, objectFit: 'cover', border: `2px solid ${theme.colors.mainColor}` }} />
                <div
                  onClick={() => { setCorrEvidence(null); setCorrEvidencePreview(null); }}
                  style={{ position: 'absolute', top: -8, right: -8, backgroundColor: '#EF4444', width: 22, height: 22, borderRadius: 11, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', border: '2px solid white' }}
                >
                  <CloseIcon size={12} color="white" />
                </div>
              </div>
            ) : (
              <button
                onClick={() => corrEvidenceRef.current?.click()}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '10px 16px', borderRadius: 10, border: '1.5px dashed #D1D5DB',
                  backgroundColor: '#F9FAFB', cursor: 'pointer', color: '#6B7280', fontSize: 13,
                }}
              >
                <CameraIcon size={18} color="#9CA3AF" />
                {tCorr.evidenceButton || 'Pilih Foto'}
              </button>
            )}
          </div>

          {/* Submit */}
          <button
            onClick={submitCorrectionForm}
            disabled={isSubmittingCorr}
            style={{
              width: '100%', padding: 14, borderRadius: 14, border: 'none',
              backgroundColor: isSubmittingCorr ? '#D1D5DB' : theme.colors.mainColor,
              color: 'white', fontSize: 15, fontWeight: 'bold',
              cursor: isSubmittingCorr ? 'not-allowed' : 'pointer',
            }}
          >
            {isSubmittingCorr ? translate.common.processing : (tCorr.submit || 'Ajukan Koreksi')}
          </button>
        </div>
      </div>
    );
  };

  const renderConfirmModal = () => {
    if (!confirmConfig.isOpen) return null;
    return (
      <div style={{ position: 'fixed', inset: 0, zIndex: 3500, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 20 }}>
        <div style={{ backgroundColor: 'white', borderRadius: 16, padding: 24, width: '100%', maxWidth: 320, boxShadow: '0 10px 25px rgba(0,0,0,0.1)' }}>
          <text.H5 style={{ marginBottom: 12 }}>{tCorr.cancelRequest || 'Batalkan Pengajuan'}</text.H5>
          <text.T16 style={{ color: theme.colors.bodyTextColor, lineHeight: 1.5, marginBottom: 24 }}>{confirmConfig.message}</text.T16>
          <div style={{ display: 'flex', gap: 10 }}>
            <button
              onClick={hideConfirm}
              style={{
                flex: 1, padding: '12px 0', borderRadius: 12,
                border: '1px solid #E5E7EB', backgroundColor: '#F9FAFB',
                color: theme.colors.mainDark, fontSize: 14, fontWeight: '600', cursor: 'pointer',
              }}
            >
              {(translate.common as any)?.no || 'Tidak'}
            </button>
            <button
              onClick={() => { hideConfirm(); confirmConfig.onConfirm(); }}
              style={{
                flex: 1, padding: '12px 0', borderRadius: 12,
                border: 'none', backgroundColor: '#EF4444',
                color: 'white', fontSize: 14, fontWeight: '600', cursor: 'pointer',
              }}
            >
              {(translate.common as any)?.yes || 'Ya, Batalkan'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderAlert = () => {
    if (!alertConfig.isOpen) return null;
    return (
      <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 4000, display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 20 }}>
        <div style={{ backgroundColor: 'white', borderRadius: 16, padding: 24, width: '100%', maxWidth: 320, textAlign: 'center', boxShadow: '0 10px 25px rgba(0,0,0,0.1)', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <div style={{marginBottom: 16}}>
            {alertConfig.type === 'success' && <CheckCircleIcon size={60} color="#10B981" />}
            {alertConfig.type === 'error' && <ErrorIcon size={60} color="#EF4444" />}
            {alertConfig.type === 'warning' && <WarningIcon size={60} color="#F59E0B" />}
          </div>
          <text.H4 style={{ marginBottom: 8 }}>{alertConfig.title}</text.H4>
          <text.T16 style={{ marginBottom: 24, color: theme.colors.bodyTextColor, lineHeight: 1.5 }}>{alertConfig.message}</text.T16>
          <components.Button title={translate.common.ok} onClick={hideAlert} containerStyle={{width: '100%'}} />
        </div>
      </div>
    );
  };

  const renderImagePreviewModal = () => {
    if (!previewImage) return null;
    
    return (
      <div 
        style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.85)', zIndex: 5000,
          display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 20
        }}
        onClick={() => setPreviewImage(null)}
      >
        <div style={{ position: 'relative', maxWidth: '100%', maxHeight: '100%' }}>
          <div 
            style={{
              position: 'absolute', top: -40, right: 0, cursor: 'pointer',
              backgroundColor: 'rgba(255,255,255,0.2)', borderRadius: '50%', padding: 4, display: 'flex'
            }}
            onClick={() => setPreviewImage(null)}
          >
            <CloseIcon size={24} color="white" />
          </div>
          <img 
            src={previewImage} 
            alt="Preview" 
            style={{ maxWidth: '100%', maxHeight: '80vh', objectFit: 'contain', borderRadius: 8 }} 
            onClick={(e) => e.stopPropagation()} // Mencegah klik pada gambar menutup modal
          />
        </div>
      </div>
    );
  };

  return (
    <div id='screen' style={{ backgroundColor: '#FFFFFF', minHeight: '100vh' }}>
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
      {renderHeader()}
      <main
        id="scrollableDiv"
        className='container'
        style={{ marginTop: 60, paddingBottom: 110, height: 'calc(100vh - 60px)', overflowY: 'auto' }}
      >
        {renderAttendanceCard()}
        {renderFilters()}
        {renderList()}
      </main>
      {/* <components.BottomTabBar /> */}
      {renderCorrectionModal()}
      {renderConfirmModal()}
      {renderAlert()}
      {renderImagePreviewModal()}

      {/* Photo Source Bottom Sheet — native only */}
      {showPhotoSourceSheet && (
        <div
          onClick={() => setShowPhotoSourceSheet(false)}
          style={{
            position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.45)',
            zIndex: 6000, display: 'flex', alignItems: 'flex-end',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: '100%', backgroundColor: 'white',
              borderRadius: '20px 20px 0 0',
              padding: '8px 0 32px',
              boxShadow: '0 -4px 24px rgba(0,0,0,0.12)',
            }}
          >
            {/* Handle */}
            <div style={{ display: 'flex', justifyContent: 'center', padding: '12px 0 8px' }}>
              <div style={{ width: 40, height: 4, borderRadius: 2, backgroundColor: '#D1D5DB' }} />
            </div>

            {/* Camera */}
            <div
              onClick={() => {
                setShowPhotoSourceSheet(false);
                const isNativeNow = isNative || !!(window as any).Capacitor?.isNativePlatform?.();
                if (isNativeNow) handleCapacitorPhoto('camera');
                else cameraInputRef.current?.click();
              }}
              style={{
                display: 'flex', alignItems: 'center', gap: 16,
                padding: '16px 24px', cursor: 'pointer',
              }}
            >
              <div style={{
                width: 44, height: 44, borderRadius: 12,
                backgroundColor: '#EFF6FF', display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <CameraIcon size={22} color="#3B82F6" />
              </div>
              <text.H5 style={{ color: theme.colors.mainDark }}>
                {(translate.attendance as any)?.openCamera || 'Buka Kamera'}
              </text.H5>
            </div>

            <div style={{ height: 1, backgroundColor: '#F3F4F6', margin: '0 24px' }} />

            {/* Gallery */}
            <div
              onClick={() => {
                setShowPhotoSourceSheet(false);
                const isNativeNow = isNative || !!(window as any).Capacitor?.isNativePlatform?.();
                if (isNativeNow) handleCapacitorPhoto('gallery');
                else fileInputRef.current?.click();
              }}
              style={{
                display: 'flex', alignItems: 'center', gap: 16,
                padding: '16px 24px', cursor: 'pointer',
              }}
            >
              <div style={{
                width: 44, height: 44, borderRadius: 12,
                backgroundColor: '#F0FDF4', display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                  <rect x="3" y="3" width="18" height="18" rx="3" stroke="#10B981" strokeWidth="2"/>
                  <circle cx="8.5" cy="8.5" r="1.5" fill="#10B981"/>
                  <path d="M3 15l5-5 4 4 3-3 6 6" stroke="#10B981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <text.H5 style={{ color: theme.colors.mainDark }}>
                {(translate.attendance as any)?.chooseFromGallery || 'Pilih dari Galeri'}
              </text.H5>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};